import { FC } from "react";
import styled from "styled-components";

import { greyMui, rydeColors } from "@ryde/theme";

export interface IPieLegendItem {
  label: string;
  color: string;
  /** Whole-number percentage share of the total. */
  share: number;
}

interface IDashboardPieLegendProps {
  items: IPieLegendItem[];
  isRtl: boolean;
  /** Rows shown before overflowing into the "+N" hover popup. */
  maxVisible?: number;
}

const DEFAULT_MAX_VISIBLE = 10;

/**
 * Custom donut legend: lists each unit with its color + share. When there are
 * more than `maxVisible` units the extra ones move into a popup revealed by
 * hovering (or keyboard-focusing) the "+N" trigger, so a long list never
 * overflows/clips the card. The popup is shown purely via CSS
 * (`:hover` / `:focus-within`) — no JS handlers, so it stays accessible.
 */
export const DashboardPieLegend: FC<IDashboardPieLegendProps> = ({
  items,
  isRtl,
  maxVisible = DEFAULT_MAX_VISIBLE,
}) => {
  const visible = items.slice(0, maxVisible);
  const rest = items.slice(maxVisible);

  const renderRow = (item: IPieLegendItem, key: string): JSX.Element => (
    <li key={key} className="legend-row">
      <span className="dot" style={{ backgroundColor: item.color }} />
      <span className="text" title={`${item.label} (${item.share}%)`}>
        {item.label} ({item.share}%)
      </span>
    </li>
  );

  return (
    <LegendStyled $rtl={isRtl}>
      {visible.map((item, index) => renderRow(item, `${item.label}-${index}`))}

      {rest.length > 0 && (
        <li className="legend-row more">
          <button type="button" className="more-trigger">
            {`+${rest.length}`}
          </button>
          <ul className="rest-popup">
            {rest.map((item, index) =>
              renderRow(item, `${item.label}-rest-${index}`),
            )}
          </ul>
        </li>
      )}
    </LegendStyled>
  );
};

const LegendStyled = styled.ul<{ $rtl: boolean }>`
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 4px;
  max-width: 55%;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: 11px;
  direction: ${({ $rtl }) => ($rtl ? "rtl" : "ltr")};

  .legend-row {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  .dot {
    flex: 0 0 auto;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }

  .text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .more {
    position: relative;
  }

  .more-trigger {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-weight: 600;
    color: ${greyMui._700};
    cursor: pointer;
  }

  .rest-popup {
    position: absolute;
    bottom: 100%;
    ${({ $rtl }) => ($rtl ? "right: 0;" : "left: 0;")}
    z-index: 10;
    display: none;
    flex-direction: column;
    gap: 4px;
    max-height: 180px;
    margin: 0 0 4px;
    padding: 8px 10px;
    overflow-y: auto;
    list-style: none;
    background: ${rydeColors.white};
    border: 1px solid ${greyMui._300};
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  .more:hover .rest-popup,
  .more:focus-within .rest-popup {
    display: flex;
  }
`;
